import { writable, get } from 'svelte/store';
import type { FavoriteView, NavbarConfig } from '../types';
import { DEFAULT_NAVBAR_VIEWS } from '../types';
import {
  createFavoriteView as apiCreate,
  updateFavoriteView as apiUpdate,
  deleteFavoriteView as apiDelete,
  reorderFavoriteViews as apiReorder,
  updateNavbarConfig as apiUpdateNavbar,
} from '../favoritesApi';
import { optimisticMutation } from '../optimisticMutation';

const DEFAULT_CONFIG: NavbarConfig = {
  show_default_views: true,
  hidden_default_views: [],
  navbar_views: DEFAULT_NAVBAR_VIEWS,
};

// Stores
export const favoriteViews = writable<FavoriteView[]>([]);
export const navbarConfig = writable<NavbarConfig>(DEFAULT_CONFIG);

// Initialize from Inertia shared props
export function initFavorites(views: FavoriteView[], config: NavbarConfig) {
  favoriteViews.set(views || []);
  navbarConfig.set(config ? { ...DEFAULT_CONFIG, ...config } : DEFAULT_CONFIG);
}

// CRUD operations (optimistic + API sync)

export async function addFavorite(
  attrs: Omit<FavoriteView, 'id' | 'position'>,
): Promise<FavoriteView> {
  // No optimistic update — waits for server response
  const result = await apiCreate(attrs);
  favoriteViews.set(result.favoriteViews);
  return result.favoriteView;
}

export async function editFavorite(
  id: string,
  attrs: Partial<Omit<FavoriteView, 'id' | 'position'>>,
): Promise<void> {
  const result = await optimisticMutation({
    getState: () => get(favoriteViews),
    applyOptimistic: (views) => views.map((v) => (v.id === id ? { ...v, ...attrs } : v)),
    syncState: (views) => favoriteViews.set(views),
    apiCall: () => apiUpdate(id, attrs),
    onSuccess: (_, apiResult) => apiResult.favoriteViews,
  });
  if (!result.success) throw result.cause;
}

export async function removeFavorite(id: string): Promise<void> {
  const result = await optimisticMutation({
    getState: () => get(favoriteViews),
    applyOptimistic: (views) => views.filter((v) => v.id !== id),
    syncState: (views) => favoriteViews.set(views),
    apiCall: () => apiDelete(id),
    onSuccess: (_, apiResult) => apiResult.favoriteViews,
  });
  if (!result.success) throw result.cause;
}

export async function reorderFavorites(orderedIds: string[]): Promise<void> {
  const result = await optimisticMutation({
    getState: () => get(favoriteViews),
    applyOptimistic: (views) => {
      const idToView = new Map(views.map((v) => [v.id, v]));
      return orderedIds
        .map((id) => idToView.get(id))
        .filter((v): v is FavoriteView => !!v)
        .map((v, i) => ({ ...v, position: i }));
    },
    syncState: (views) => favoriteViews.set(views),
    apiCall: () => apiReorder(orderedIds),
    onSuccess: (_, apiResult) => apiResult.favoriteViews,
  });
  if (!result.success) throw result.cause;
}

export async function setNavbarConfig(config: Partial<NavbarConfig>): Promise<void> {
  const result = await optimisticMutation({
    getState: () => get(navbarConfig),
    applyOptimistic: (c) => ({ ...c, ...config }),
    syncState: (c) => navbarConfig.set(c),
    apiCall: () => apiUpdateNavbar(config),
    onSuccess: (_, apiResult) => apiResult.navbarConfig,
  });
  if (!result.success) throw result.cause;
}
